import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { eventPage, isEntertainmentButNotSplash, isSplash, updateBookedEntertainments } from '../../utils';
import NewDialog from '../newDialog/NewDialog';
import DialogMessage from '../dialogMessage/DialogMessage';
import Dialog from '../dialog/Dialog';
import './BookedEventCard.scss';

const getRemainingTime = (startTime = '') => {
  const [hour = '0', minute = '0'] = startTime.split(':');
  const startHour = parseInt(hour, 10);
  const startMinute = parseInt(minute, 10);
  const currentHour = moment().hour();
  const currentMinute = moment().minutes();
  if (startHour < currentHour) return 0;
  if (startHour === currentHour && startMinute < currentMinute) return 0;
  const currentTime = moment().format('HH:mm');
  const startTimeFormat = moment(startTime, 'HH:mm').format('HH:mm');
  const timeToEvent = moment.duration(moment(startTimeFormat, 'HH:mm').diff(moment(currentTime, 'HH:mm')));
  const hours = parseInt(timeToEvent.asHours(), 10);
  const minutes = parseInt(timeToEvent.asMinutes(), 10) - hours * 60;
  const remainingTime = {
    hours,
    minutes,
  };

  return remainingTime;
};

const getTimeText = remainingTime => (remainingTime <= 1 ? 'hr' : 'hrs');
const getMinuteText = remainingMinute => (remainingMinute <= 1 ? 'min' : 'mins');

const handleCancelOnSchedule = async (
  resId,
  siteCode,
  eventId,
  eventDate,
  startTime,
  endTime,
  name,
  deleteEntertainment,
  getBookedEntertainments,
  getAvailableEntertainments,
  removeDining,
  isEntertainment,
  setShowCancelDialog,
) => {
  if (isEntertainment) {
    await deleteEntertainment(resId, siteCode, eventId, eventDate, startTime, endTime, name);
  } else {
    await removeDining(eventId, resId);
  }
  updateBookedEntertainments(resId, siteCode, getBookedEntertainments, getAvailableEntertainments);
  setShowCancelDialog(false);
};

const BookedEventCard = ({
  eventId,
  name,
  location,
  startTime,
  endTime,
  slotColor = 'slot-color-1',
  isHappeningToday,
  isHappeningNext,
  numOfGuests,
  isPastEvent = false,
  isBMEItem = false,
  deleteEntertainment,
  getBookedEntertainments,
  getAvailableEntertainments,
  startDate,
  resId,
  siteCode,
  removeDining,
  isEntertainment,
  isDineAroundItem,
}) => {
  const [remainingTime, setRemainingTime] = useState(getRemainingTime(startTime));
  const [showArrivalMessageDialog, setShowArrivalMessageDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const showEditAndCancelBtn = isBMEItem || isDineAroundItem; // basically DA items are not bme items

  useEffect(() => {
    const intervalId = setInterval(() => {
      const time = getRemainingTime(startTime);
      setRemainingTime(time);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [startTime]);

  return (
    <>
      {
        showArrivalMessageDialog && (
          <Dialog
            messages={['Arriving a bit early allows us time to get all our guests seated safely and comfortably',
              'If you do not turn up 15 minutes before the show starts you may lose your place']}
            positiveButtonText="OK"
            onPositiveButtonClick={() => setShowArrivalMessageDialog(false)}
            hideNegativeButton
            icon="timer"
            opacity
          />
        )
      }
      {
        showCancelDialog && (
          <NewDialog
            positiveButtonText="YES"
            negativeButtonText="NO"
            onPositiveButtonClick={() => handleCancelOnSchedule(
              resId,
              siteCode,
              eventId,
              startDate,
              startTime,
              endTime,
              name,
              deleteEntertainment,
              getBookedEntertainments,
              getAvailableEntertainments,
              removeDining,
              isEntertainment,
              setShowCancelDialog,
            )}
            onNegativeButtonClick={() => setShowCancelDialog(false)}
            onBackArrowClick={() => setShowCancelDialog(false)}
          >
            <DialogMessage
              messages={['Are you sure you want to cancel the following activity?']}
              date={`${name} ${moment(startDate).format('dddd Do MMMM')} ${startTime}`}
            />
          </NewDialog>
        )
      }
      <div className={classnames('booked-event', { today: isHappeningToday }, { next: isHappeningNext }, { past: isPastEvent }, slotColor)}>
        <div className="time-block">
          <div className={classnames('start-time', { next: isHappeningNext }, { past: isPastEvent }, slotColor)}>
            <div className="clock-icon"><FontAwesomeIcon icon={faClock} size="lg" /></div>
            <div className="time">{startTime}</div>
          </div>
          {!!endTime && <div className={classnames('end-time', { next: isHappeningNext }, { past: isPastEvent }, slotColor)}>{endTime}</div>}
        </div>
        <div className="event-body">
          {
            !!isHappeningNext && (remainingTime.hours >= 0 && remainingTime.minutes >= 0) && (
              <>
                <div className={classnames('countdown', { next: isHappeningNext })}>
                  {`Starts in ${remainingTime.hours}${getTimeText(remainingTime.hours)} `}
                  {remainingTime.minutes > 0 ? `${remainingTime.minutes} ${getMinuteText(remainingTime.minutes)}` : ''}
                </div>
                {isEntertainmentButNotSplash(name) && (
                  <div className="arrive-message" onClick={() => setShowArrivalMessageDialog(true)}>
                    YOU MUST ARRIVE 15 MINUTES EARLY
                  </div>
                )}
              </>
            )
          }
          <div className={classnames('name', { next: isHappeningNext })}>{name}</div>
          {!!location && <div className={classnames('location', { next: isHappeningNext })}>{location}</div>}
          {!isPastEvent && (
            <div className={classnames('details', { next: isHappeningNext })}>
              {showEditAndCancelBtn && (<Link to={`${eventPage}&eventId=${eventId}`}>View details</Link>)}
            </div>
          )}
        </div>
        <div className="event-right-container">
          <div className={classnames('guest-number', { past: isPastEvent, next: isHappeningNext })}>
            {isSplash(name) && `(${numOfGuests} guests)`}
          </div>
          {!isPastEvent && showEditAndCancelBtn && (
            <div
              className={classnames('cancel-btn', { next: isHappeningNext })}
              onClick={() => setShowCancelDialog(true)}
            >
              Cancel booking
            </div>
          )}
        </div>
      </div>
    </>
  );
};

BookedEventCard.propTypes = {
  eventId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  location: PropTypes.string,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string,
  slotColor: PropTypes.string,
  isHappeningToday: PropTypes.bool,
  isHappeningNext: PropTypes.bool,
  numOfGuests: PropTypes.number,
  isPastEvent: PropTypes.bool,
  isBMEItem: PropTypes.bool,
  deleteEntertainment: PropTypes.func.isRequired,
  getBookedEntertainments: PropTypes.func.isRequired,
  getAvailableEntertainments: PropTypes.func.isRequired,
  removeDining: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  resId: PropTypes.number.isRequired,
  siteCode: PropTypes.string.isRequired,
  isEntertainment: PropTypes.bool,
  isDineAroundItem: PropTypes.bool,
};

export default BookedEventCard;
